// Authentication Store - Pinia Setup Store Pattern
// Handles login, logout, token refresh, and privilege checking

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { LoginRequest, TokensResponse, RefreshTokenRequest, AuthUser } from '@/types/auth';
import { useApiClient, resetApiClient } from '@/services/apiClient';
import { useToastStore } from '@/stores/toastStore';
import { ApiRoutes } from '@/constants/apiConstants';
import { RoleConstants } from '@/constants/roleConstants';

const TOKENS_STORAGE_KEY = 'ruumi_auth_tokens';
const USER_STORAGE_KEY = 'ruumi_auth_user';
const OLD_TOKENS_KEY = 'ruumi_tokens';
const OLD_USER_KEY = 'ruumi_user';
const MAX_REFRESH_RETRIES = 3;
const TOKEN_LIFETIME_MINUTES = 30; // Must match backend setting
const REFRESH_BEFORE_EXPIRY_MINUTES = 5; // Refresh 5 minutes before expiry

export const useAuthenticationStore = defineStore('authentication', () => {
  // State
  const tokens = ref<TokensResponse | null>(null);
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  let tokenRefreshTimer: number | null = null;

  // Getters
  const isAuthenticated = computed(() => !!tokens.value?.token);
  const currentUser = computed(() => user.value);
  const userPrivileges = computed(() => user.value?.privileges || []);
  const isClientRole = computed(() => user.value?.roleName?.toUpperCase() === RoleConstants.Client);
  const canViewAllEntries = computed(() => !!user.value?.canViewAllEntries);
  const employeeId = computed(() => user.value?.employeeId);
  const clientId = computed(() => user.value?.clientId);
  const isRestrictedEmployeeUser = computed(() => !!employeeId.value && !canViewAllEntries.value);
  // Waitlisted bookings are visible only to Administrator and Receptionist roles
  const canManageWaitlistedBookings = computed(() => {
    const roleName = user.value?.roleName?.toUpperCase();
    return roleName === RoleConstants.Administrator || roleName === RoleConstants.Receptionist;
  });

  /**
   * Start automatic token refresh timer
   * Refreshes token before it expires to maintain session
   */
  const startTokenRefreshTimer = (): void => {
    // Clear existing timer
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
    // Calculate when to refresh (before expiry)
    const refreshInMs = (TOKEN_LIFETIME_MINUTES - REFRESH_BEFORE_EXPIRY_MINUTES) * 60 * 1000;
        
    tokenRefreshTimer = window.setTimeout(async () => {
      const success = await tryRefreshToken();
      
      if (success) {
        // Schedule next refresh
        startTokenRefreshTimer();
      } else {
        console.error('Automatic token refresh failed');
      }
    }, refreshInMs);
  };

  /**
   * Stop automatic token refresh timer
   */
  const stopTokenRefreshTimer = (): void => {
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
      tokenRefreshTimer = null;
    }
  };

  /**
   * Initialize authentication from localStorage
   */
  const initializeAuth = (): void => {
    try {
      // Check for old localStorage keys and clear them silently
      const oldTokens = localStorage.getItem(OLD_TOKENS_KEY);
      const oldUser = localStorage.getItem(OLD_USER_KEY);
      
      if (oldTokens || oldUser) {
        localStorage.removeItem(OLD_TOKENS_KEY);
        localStorage.removeItem(OLD_USER_KEY);
        // Silently redirect to login without notification
        return;
      }

      const storedTokens = localStorage.getItem(TOKENS_STORAGE_KEY);
      const storedUser = localStorage.getItem(USER_STORAGE_KEY);

      if (storedTokens && storedUser) {
        tokens.value = JSON.parse(storedTokens);
        user.value = JSON.parse(storedUser);
        
        // Start automatic token refresh
        startTokenRefreshTimer();
      }
    } catch (err) {
      console.error('Failed to initialize auth:', err);
      clearAuth();
    }
  };

  /**
   * Login with username and password
   */
  const login = async (credentials: LoginRequest): Promise<boolean> => {
    loading.value = true;
    error.value = null;

    try {
      const { apiClient } = await useApiClient();
      const response = await apiClient.post<TokensResponse>(
        ApiRoutes.Auth.Login,
        credentials
      ) as unknown as TokensResponse;

      // Store tokens (keep internal structure with Token/RefreshToken for compatibility)
      tokens.value = {
        token: response.token,
        refreshToken: response.refreshToken,
        privileges: response.privileges,
        uId: response.uId,
        username: response.username,
        displayName: response.displayName,
        roleName: response.roleName,
        canViewAllEntries: response.canViewAllEntries,
        employeeId: response.employeeId,
        clientId: response.clientId
      };
      
      // Map response to user object
      user.value = {
        userId: response.uId,
        username: response.username,
        fullName: response.displayName,
        roleName: response.roleName,
        privileges: response.privileges,
        canViewAllEntries: response.canViewAllEntries,
        employeeId: response.employeeId,
        clientId: response.clientId
      };

      // Persist to localStorage
      localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens.value));
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user.value));

      // Start automatic token refresh timer
      startTokenRefreshTimer();

      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed';
      clearAuth();
      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Logout and clear authentication state
   */
  const logout = async (): Promise<void> => {
    stopTokenRefreshTimer();
    clearAuth();
    resetApiClient();
    
    // Redirect to login page
    window.location.href = '/login';
  };

  /**
   * Try to refresh the access token using refresh token
   * Implements retry logic with exponential backoff (3 attempts)
   */
  const tryRefreshToken = async (retryCount = 0): Promise<boolean> => {
    if (!tokens.value?.refreshToken) {
      return false;
    }

    try {
      const { apiClient } = await useApiClient();
      const request: RefreshTokenRequest = {
        accessToken: tokens.value.token,
        refreshToken: tokens.value.refreshToken
      };

      const response = await apiClient.post<TokensResponse>(
        ApiRoutes.Auth.RefreshToken,
        request
      ) as unknown as TokensResponse;

      // Store new tokens
      tokens.value = {
        token: response.token,
        refreshToken: response.refreshToken,
        privileges: response.privileges,
        uId: response.uId,
        username: response.username,
        displayName: response.displayName,
        roleName: response.roleName,
        canViewAllEntries: response.canViewAllEntries,
        employeeId: response.employeeId,
        clientId: response.clientId
      };

      // Update user object
      user.value = {
        userId: response.uId,
        username: response.username,
        fullName: response.displayName,
        roleName: response.roleName,
        privileges: response.privileges,
        canViewAllEntries: response.canViewAllEntries,
        employeeId: response.employeeId,
        clientId: response.clientId
      };

      // Update localStorage
      localStorage.setItem(TOKENS_STORAGE_KEY, JSON.stringify(tokens.value));
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user.value));

      return true;
    } catch (err) {
      console.error(`Token refresh failed (attempt ${retryCount + 1}/${MAX_REFRESH_RETRIES}):`, err);
      
      // Retry logic with exponential backoff
      if (retryCount < MAX_REFRESH_RETRIES - 1) {
        const delayMs = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return tryRefreshToken(retryCount + 1);
      }
      
      // All retries exhausted - show session expired message and logout
      const toastStore = useToastStore();
      toastStore.setToastErrorMessage('Session expired', 'Please login again');
      clearAuth();
      
      // Redirect to login after a short delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
      
      return false;
    }
  };

  /**
   * Check if user has a specific privilege
   */
  const hasPrivilege = (privilegeCode: string): boolean => {
    return userPrivileges.value.includes(privilegeCode);
  };

  /**
   * Check if user has any of the specified privileges
   */
  const hasAnyPrivilege = (privilegeCodes: string[]): boolean => {
    return privilegeCodes.some(code => hasPrivilege(code));
  };

  /**
   * Check if user has all of the specified privileges
   */
  const hasAllPrivileges = (privilegeCodes: string[]): boolean => {
    return privilegeCodes.every(code => hasPrivilege(code));
  };

  /**
   * Clear authentication state
   */
  const clearAuth = (): void => {
    stopTokenRefreshTimer();
    tokens.value = null;
    user.value = null;
    error.value = null;
    localStorage.removeItem(TOKENS_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  };

  /**
   * Reset error state
   */
  const resetError = (): void => {
    error.value = null;
  };

  // Initialize on store creation
  initializeAuth();

  return {
    // State
    tokens,
    user,
    loading,
    error,
    // Getters
    isAuthenticated,
    currentUser,
    userPrivileges,
    isClientRole,
    canViewAllEntries,
    employeeId,
    clientId,
    isRestrictedEmployeeUser,
    canManageWaitlistedBookings,
    // Actions
    login,
    logout,
    tryRefreshToken,
    hasPrivilege,
    hasAnyPrivilege,
    hasAllPrivileges,
    resetError,
    initializeAuth
  };
});
